'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'

import ErrorToast from '@/core/components/error-toast'
import { Button } from '@/core/components/form/button/button'
import SessionSnapshotProvider from '@/features/veluvit/components/providers/session-snapshot-provider'
import { AppNowProvider } from '@/features/veluvit/hooks/use-app-now'

import LogoLoader from '@/features/veluvit/components/ui/logo-loader'
import { CatalogContext } from '@/features/veluvit/hooks/use-catalog'
import { buildLiveCatalog } from '@/features/veluvit/lib/live-catalog'
import { catalogEnvelopeSchema } from '@/features/veluvit/schemas/catalog.schema'
import {
  acceptInvite,
  holdInvite,
} from '@/features/veluvit/services/creator-invite'
import {
  restoreBrowserSession,
  startSession,
} from '@/features/veluvit/services/session.service'
import { finishSocialLogin } from '@/features/veluvit/services/social-login'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { Bootstrap } from '@/features/veluvit/types/api.types'
import type { FetcherResponse } from '@/core/lib/fetcher/core'

type DataProviderProps = {
  children: React.ReactNode
  initial?: FetcherResponse<Bootstrap>
  epoch?: string
  initialNow?: number
}

/** 스튜디오에서 bootstrap 이 실패했을 때의 빈 목록 — 매 렌더 새 배열이면 카탈로그가 매번 다시 만들어진다 */
const NO_WORKS: never[] = []
const EMPTY_CONFIG: Bootstrap['config'] = {
  offline: false,
  testCharging: false,
  prices: { inner: 0, record: 0, suggestions: 0, snap: 0 },
}
/** Bootstrap 공개 작품이 준비되면 화면 카탈로그를 만든다. 빈 공개 목록도 정상 상태다. */
export default function DataProvider({
  children,
  initial,
  epoch,
  initialNow,
}: DataProviderProps) {
  const content = (
    <DataProviderContent serverInitial={!!(initial && epoch)}>
      {children}
    </DataProviderContent>
  )
  return (
    <AppNowProvider initialNow={initialNow ?? 0}>
      {initial && epoch ? (
        <SessionSnapshotProvider initial={initial} epoch={epoch}>
          {content}
        </SessionSnapshotProvider>
      ) : (
        content
      )}
    </AppNowProvider>
  )
}

function DataProviderContent({
  children,
  serverInitial = false,
}: {
  children: React.ReactNode
  serverInitial?: boolean
}) {
  const ready = useSessionStore((s) => s.ready)
  const sessionError = useSessionStore((s) => s.error)
  const works = useSessionStore((s) => s.data?.works)
  const ownedCards = useSessionStore((s) => s.data?.ownedCards)
  const modes = useSessionStore((s) => s.data?.config.modes)
  const userName = useSessionStore((s) => s.data?.user?.name)
  const needsRestoration = useSessionStore(
    (s) => s.data?.sessionRestorationRequired === true
  )
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (serverInitial && attempt === 0 && !useSessionStore.getState().ready)
      return
    let alive = true
    const identity = useSessionStore.getState().identity
    // CSRF 준비는 실제 쓰기 요청이 전송되기 전에 성공 여부까지 확인한다.
    startSession(attempt > 0)
      .then((data) => {
        if (!alive) return
        if (useSessionStore.getState().data !== data)
          useSessionStore.getState().applyBootstrap(data)
        // 소셜 로그인에서 돌아온 경우 결과를 알리고 떠났던 화면으로 보낸다
        finishSocialLogin(data.user)
        // 작가 초대 링크로 들어왔으면 코드를 맡아 둔다(회원이 되면 아래 effect 가 등록한다)
        holdInvite()
      })
      .catch((error: Error) => {
        if (alive && useSessionStore.getState().identity === identity)
          useSessionStore.getState().setError(error.message)
      })
    return () => {
      alive = false
    }
  }, [attempt, serverInitial])

  useEffect(() => {
    if (!needsRestoration) return
    const identity = useSessionStore.getState().identity
    void restoreBrowserSession().catch((error: unknown) => {
      if (useSessionStore.getState().identity === identity)
        useSessionStore
          .getState()
          .setError(error instanceof Error ? error.message : '세션 복원 실패')
    })
  }, [needsRestoration])

  // 회원(게스트 아님)이 되면 맡아 둔 작가 초대를 등록한다 — 로그인·가입·소셜 로그인 모두 여기로 모인다
  const member = useSessionStore((s) =>
    s.data?.user && !s.data.user.guest ? s.data.user.id : null
  )
  useEffect(() => {
    if (member) void acceptInvite()
  }, [member])

  // 제작·결제 화면은 공개 작품 목록 없이도 열린다. 결제 화면의 세션 복원·오류 처리는 PaymentScreen이 맡는다.
  const pathname = usePathname()
  const independent =
    pathname.startsWith('/studio-renew') || pathname.startsWith('/payment/')
  const liveWorks = works ?? (independent ? NO_WORKS : undefined)

  const catalog = useMemo(
    () =>
      liveWorks
        ? buildLiveCatalog(
            liveWorks,
            { ...EMPTY_CONFIG, modes },
            userName,
            ownedCards
          )
        : null,
    [liveWorks, modes, userName, ownedCards]
  )
  const valid = useMemo(
    () => (catalog ? catalogEnvelopeSchema.safeParse(catalog).success : null),
    [catalog]
  )
  const failed = valid === false || (!!sessionError && !independent)

  if (failed || !catalog || (!ready && !independent)) {
    return (
      // 헤더까지 덮는 검정 전체 화면 — 헤더(검정)와 본문(남색)이 갈라져 보이던 것을 한 판으로
      <div
        role={failed ? undefined : 'status'}
        aria-live="polite"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-3.5 bg-background p-6 text-center text-t1"
      >
        {/* 불러오는 중에는 글자 없이 로고 모션만(안내는 화면 읽기용), 실패 이유는 토스트 · 화면엔 다시 시도 버튼만 */}
        {!failed && <LogoLoader label="이야기를 불러오고 있어요" />}
        {failed && (
          <>
            <ErrorToast
              message={
                sessionError ||
                '이야기를 불러오지 못했어요. 연결을 확인한 뒤 다시 시도해 주세요.'
              }
            />
            <Button
              variant="accent"
              size="lg"
              shape="pill"
              bold
              className="w-full max-w-70"
              onClick={() => {
                useSessionStore.getState().setError(null)
                setAttempt((n) => n + 1)
              }}
            >
              다시 불러오기
            </Button>
          </>
        )}
      </div>
    )
  }

  return (
    <CatalogContext.Provider value={catalog}>
      {children}
    </CatalogContext.Provider>
  )
}
